import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Box, Typography, Chip, IconButton, Tooltip, CircularProgress,
  TextField, MenuItem, Stack, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert,
} from '@mui/material';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MicIcon from '@mui/icons-material/Mic';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TableKit } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import AudioRecorder from '../../components/AudioRecorder.jsx';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';

const MODE_LABELS = { file: 'קובץ קול', tts: 'TTS' };
const STATUS_LABELS = { active: 'פעיל', unused: 'לא בשימוש' };
const AUDIO_EXT_RE = /\.(mp3|wav|ogg|m4a|aac|flac|wma|webm)$/i;

const isAudioFile = (file) => {
  if (!file) return false;
  if (file.type?.startsWith('audio/')) return true;
  return AUDIO_EXT_RE.test(file.name || '');
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const NarrationCatalog = () => {
  const inNextDesign = useCkInside();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [extensionFilter, setExtensionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [yemotFilter, setYemotFilter] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [confirmSwitch, setConfirmSwitch] = useState(null);
  const [recordRow, setRecordRow] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [pendingDrop, setPendingDrop] = useState(null);
  const audioElRef = useRef(null);
  const fileInputRefs = useRef({});
  const dragCounterRef = useRef({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/narration-prompts', {
        params: { all: '1', sort: 'code', order: 'asc' },
      });
      setAllData(res.data || []);
    } catch (err) {
      console.error('שגיאה בשליפת קטלוג קריינות:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => () => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    if (pendingDrop?.objectUrl) URL.revokeObjectURL(pendingDrop.objectUrl);
  }, [pendingDrop]);

  const extensions = useMemo(() => {
    const set = new Set(allData.map((r) => r.extension).filter(Boolean));
    return Array.from(set).sort();
  }, [allData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allData.filter((row) => {
      if (modeFilter && row.mode !== modeFilter) return false;
      if (extensionFilter && row.extension !== extensionFilter) return false;
      if (statusFilter && row.status !== statusFilter) return false;
      if (yemotFilter === 'yes' && !row.fileExistsOnYemot) return false;
      if (yemotFilter === 'no' && row.fileExistsOnYemot) return false;
      if (!q) return true;
      return (
        String(row.code).includes(q) ||
        (row.text || '').toLowerCase().includes(q) ||
        (row.notes || '').toLowerCase().includes(q)
      );
    });
  }, [allData, search, modeFilter, extensionFilter, statusFilter, yemotFilter]);

  const pendingDropRow = useMemo(
    () => (pendingDrop ? allData.find((r) => r._id === pendingDrop.rowId) : null),
    [allData, pendingDrop]
  );

  const stopPlay = () => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
    }
    setPlayingId(null);
  };

  const clearPendingDrop = useCallback(() => {
    setPendingDrop(null);
  }, []);

  const handleSyncYemot = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const { data } = await api.post('/narration-prompts/sync-yemot');
      setSyncMsg(
        `סנכרון הושלם: ${data.toFile} קבצים בימות, ${data.toTts} הועברו ל-TTS, ${data.yemotFileCount} קבצים בתיקיית audio`
      );
      await loadData();
    } catch (err) {
      setSyncMsg(err.response?.data?.error || err.message || 'סנכרון נכשל');
    } finally {
      setSyncing(false);
    }
  };

  const handlePlay = useCallback((id) => {
    if (playingId === id && audioElRef.current) {
      stopPlay();
      return;
    }
    stopPlay();
    api.get(`/narration-prompts/${id}/audio`, { responseType: 'blob' })
      .then(async ({ data: blob, status }) => {
        if (blob.type?.includes('application/json') || status >= 400) {
          let msg = 'לא ניתן להשמיע את הקובץ';
          try {
            const text = await blob.text();
            const json = JSON.parse(text);
            if (json?.error) msg = json.error;
          } catch { /* ignore */ }
          alert(msg);
          return;
        }
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); audioElRef.current = null; };
        audio.onerror = () => {
          setPlayingId(null);
          URL.revokeObjectURL(url);
          audioElRef.current = null;
          alert('הדפדפן לא הצליח לנגן את הקובץ');
        };
        audioElRef.current = audio;
        setPlayingId(id);
        audio.play().catch(() => {
          setPlayingId(null);
          alert('הדפדפן חסם את הנגינה');
        });
      })
      .catch(async (err) => {
        let msg = 'שגיאה בהשמעת הקובץ';
        const data = err.response?.data;
        if (data instanceof Blob) {
          try {
            const json = JSON.parse(await data.text());
            if (json?.error) msg = json.error;
          } catch { /* ignore */ }
        } else if (data?.error) {
          msg = data.error;
        }
        alert(msg);
        setPlayingId(null);
      });
  }, [playingId]);

  const handleUpload = useCallback(async (id, file) => {
    if (!file) return false;
    if (!isAudioFile(file)) {
      alert('סוג קובץ לא נתמך — יש להעלות קובץ שמע (mp3, wav, ogg, m4a, webm וכו\')');
      return false;
    }
    const formData = new FormData();
    formData.append('audio', file);
    try {
      setUploadingId(id);
      const { data: res } = await api.post(`/narration-prompts/${id}/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAllData((prev) =>
        prev.map((r) =>
          r._id === id
            ? {
              ...r,
              audioFilename: res.audioFilename,
              yemotPath: res.yemotPath,
              mode: res.mode || 'file',
              hasAudioData: true,
              fileExistsOnYemot: Boolean(res.fileExistsOnYemot),
            }
            : r
        )
      );
      return true;
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בהעלאת קובץ שמע');
      return false;
    } finally {
      setUploadingId(null);
      if (fileInputRefs.current[id]) fileInputRefs.current[id].value = '';
    }
  }, []);

  const handleRecordedAudio = useCallback(async (id, blob, code) => {
    if (!blob) return;
    const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
    const file = new File([blob], `recording-${code || id}.${ext}`, { type: blob.type || `audio/${ext}` });
    const ok = await handleUpload(id, file);
    if (ok) setRecordRow(null);
  }, [handleUpload]);

  const openDropConfirm = useCallback((rowId, file) => {
    if (!isAudioFile(file)) {
      alert('סוג קובץ לא נתמך — יש לגרור קובץ שמע (mp3, wav, ogg, m4a, webm וכו\')');
      return;
    }
    setPendingDrop({
      rowId,
      file,
      objectUrl: URL.createObjectURL(file),
    });
  }, []);

  const confirmDropUpload = async () => {
    if (!pendingDrop) return;
    const { rowId, file } = pendingDrop;
    const ok = await handleUpload(rowId, file);
    if (ok) clearPendingDrop();
  };

  const switchToTts = async (row) => {
    try {
      if (row.audioFilename || row.hasAudioData) {
        await api.delete(`/narration-prompts/${row._id}/audio`);
        setAllData((prev) =>
          prev.map((r) =>
            r._id === row._id
              ? {
                ...r,
                mode: 'tts',
                yemotPath: '',
                audioFilename: '',
                hasAudioData: false,
                fileExistsOnYemot: false,
              }
              : r
          )
        );
      } else {
        const { data } = await api.patch(`/narration-prompts/${row._id}`, { mode: 'tts' });
        setAllData((prev) => prev.map((r) => (r._id === row._id ? { ...r, ...data } : r)));
      }
      stopPlay();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה במעבר ל-TTS');
    } finally {
      setConfirmSwitch(null);
    }
  };

  const switchToFile = async (row) => {
    if (!row.fileExistsOnYemot && !row.audioFilename && !row.hasAudioData) {
      fileInputRefs.current[row._id]?.click();
      return;
    }
    try {
      const { data } = await api.patch(`/narration-prompts/${row._id}`, { mode: 'file' });
      setAllData((prev) => prev.map((r) => (r._id === row._id ? { ...r, ...data } : r)));
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה במעבר לקובץ קול');
    }
  };

  const columns = useMemo(() => [
    {
      key: 'code',
      title: 'קוד',
      sortable: true,
      minWidth: '70px',
      render: (v) => (
        <Typography variant="body2" fontWeight={700} fontFamily="monospace">{v}</Typography>
      ),
    },
    {
      key: 'text',
      title: 'טקסט',
      sortable: true,
      minWidth: '260px',
      render: (v) => (
        <Typography variant="body2" sx={{ maxWidth: 400, whiteSpace: 'normal' }}>{v || '—'}</Typography>
      ),
    },
    {
      key: 'mode',
      title: 'סוג',
      sortable: true,
      minWidth: '100px',
      render: (v) => (
        <Chip
          label={MODE_LABELS[v] || v}
          size="small"
          color={v === 'file' ? 'primary' : 'default'}
          variant={v === 'file' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      key: 'fileExistsOnYemot',
      title: 'בימות',
      sortable: true,
      minWidth: '110px',
      render: (v, row) => {
        if (row.mode === 'tts' && !v) {
          return (
            <Chip
              label="TTS"
              size="small"
              color="default"
              variant="outlined"
            />
          );
        }
        return (
          <Chip
            icon={v ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
            label={v ? 'קיים' : 'חסר'}
            size="small"
            color={v ? 'success' : 'warning'}
            variant="outlined"
          />
        );
      },
    },
    {
      key: 'extension',
      title: 'שלוחה',
      sortable: true,
      minWidth: '80px',
      render: (v) => v || '—',
    },
    {
      key: 'status',
      title: 'סטטוס',
      sortable: true,
      minWidth: '90px',
      render: (v) => (
        <Chip
          label={STATUS_LABELS[v] || v}
          size="small"
          color={v === 'active' ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      key: 'actions',
      title: 'פעולות',
      sortable: false,
      minWidth: '220px',
      render: (_v, row) => {
        const isDragOver = dragOverId === row._id;
        const isUploading = uploadingId === row._id;
        return (
          <Box
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dragCounterRef.current[row._id] = (dragCounterRef.current[row._id] || 0) + 1;
              setDragOverId(row._id);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dragCounterRef.current[row._id] = Math.max(0, (dragCounterRef.current[row._id] || 0) - 1);
              if (dragCounterRef.current[row._id] === 0) {
                setDragOverId((prev) => (prev === row._id ? null : prev));
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dragCounterRef.current[row._id] = 0;
              setDragOverId(null);
              if (isUploading) return;
              const file = e.dataTransfer.files?.[0];
              if (file) openDropConfirm(row._id, file);
            }}
            sx={{
              border: '1px dashed',
              borderColor: isDragOver ? 'primary.main' : 'transparent',
              bgcolor: isDragOver ? 'action.hover' : 'transparent',
              borderRadius: 1,
              px: 0.5,
              py: 0.25,
              minWidth: 200,
              transition: 'border-color 0.15s, background-color 0.15s',
            }}
            title={isDragOver ? 'שחרר לאישור העלאה' : undefined}
          >
            {isDragOver && (
              <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 0.5 }}>
                שחרר לאישור העלאה
              </Typography>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <input
                type="file"
                accept="audio/*"
                hidden
                ref={(el) => { fileInputRefs.current[row._id] = el; }}
                onChange={(e) => handleUpload(row._id, e.target.files?.[0])}
              />
              {(row.fileExistsOnYemot || row.audioFilename || row.hasAudioData) && (
                <Tooltip title={playingId === row._id ? 'עצור' : 'השמע'}>
                  <IconButton size="small" onClick={() => handlePlay(row._id)} color="primary">
                    {playingId === row._id ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="העלה / החלף קובץ קול">
                <span>
                  <IconButton
                    size="small"
                    disabled={isUploading}
                    onClick={() => fileInputRefs.current[row._id]?.click()}
                  >
                    {isUploading
                      ? <CircularProgress size={18} />
                      : <UploadFileIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="הקלט שמע">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={isUploading}
                    onClick={() => setRecordRow(row)}
                  >
                    <MicIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {row.mode === 'file' ? (
                <Tooltip title="החלף ל-TTS">
                  <IconButton size="small" onClick={() => setConfirmSwitch(row)}>
                    <SwapHorizIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={row.fileExistsOnYemot ? 'החלף לקובץ קול' : 'העלה קובץ כדי לעבור לקובץ קול'}>
                  <IconButton size="small" onClick={() => switchToFile(row)}>
                    <RecordVoiceOverOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {(row.audioFilename || row.hasAudioData) && (
                <Tooltip title="מחק קובץ וחזור ל-TTS">
                  <IconButton size="small" color="error" onClick={() => setConfirmSwitch(row)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>
        );
      },
    },
  ], [handlePlay, handleUpload, openDropConfirm, playingId, uploadingId, dragOverId]);

  return (
    <Box sx={{ p: inNextDesign ? 0 : 3 }}>
      <AdminPageHeader
        title="קטלוג קריינות"
        sub={`${filtered.length} מקטעים`}
        actions={(
          <Button
            variant="contained"
            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={handleSyncYemot}
            disabled={syncing}
          >
            סנכרן מול ימות
          </Button>
        )}
        legacy={(
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap">
            <RecordVoiceOverOutlinedIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>קטלוג קריינות</Typography>
            <Chip label={`${filtered.length} מקטעים`} size="small" />
            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              onClick={handleSyncYemot}
              disabled={syncing}
            >
              סנכרן מול ימות
            </Button>
          </Stack>
        )}
      />

      {syncMsg && (
        <Alert
          severity={syncMsg.includes('נכשל') ? 'error' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setSyncMsg('')}
        >
          {syncMsg}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <TextField
          size="small"
          label="חיפוש"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          select
          size="small"
          label="סוג"
          value={modeFilter}
          onChange={(e) => setModeFilter(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">הכל</MenuItem>
          <MenuItem value="file">קובץ קול</MenuItem>
          <MenuItem value="tts">TTS</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="בימות"
          value={yemotFilter}
          onChange={(e) => setYemotFilter(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">הכל</MenuItem>
          <MenuItem value="yes">קיים</MenuItem>
          <MenuItem value="no">חסר</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="שלוחה"
          value={extensionFilter}
          onChange={(e) => setExtensionFilter(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">הכל</MenuItem>
          {extensions.map((ext) => (
            <MenuItem key={ext} value={ext}>{ext}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="סטטוס"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">הכל</MenuItem>
          <MenuItem value="active">פעיל</MenuItem>
          <MenuItem value="unused">לא בשימוש</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={loadData}>רענון</Button>
      </Stack>

      <TableKit
        columns={columns}
        data={filtered}
        total={filtered.length}
        loading={loading}
        clientSideMode
        emptyMessage="אין מקטעי קריינות — יש להריץ seedNarrationPrompts"
        exportFileName="קטלוג-קריינות"
      />

      <Dialog open={Boolean(confirmSwitch)} onClose={() => setConfirmSwitch(null)}>
        <DialogTitle>החלפה ל-TTS</DialogTitle>
        <DialogContent>
          <Typography>
            להחליף את מקטע {confirmSwitch?.code} ל-TTS
            {(confirmSwitch?.audioFilename || confirmSwitch?.hasAudioData)
              ? ' ולמחוק את קובץ הקול?'
              : '?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSwitch(null)}>ביטול</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmSwitch && switchToTts(confirmSwitch)}
          >
            אישור
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(recordRow)}
        onClose={() => {
          if (uploadingId === recordRow?._id) return;
          setRecordRow(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          הקלטת שמע לקוד {recordRow?.code}
        </DialogTitle>
        <DialogContent>
          {recordRow?.text && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {recordRow.text}
            </Typography>
          )}
          {recordRow && (
            <AudioRecorder
              key={recordRow._id}
              onSave={(blob) => handleRecordedAudio(recordRow._id, blob, recordRow.code)}
              saving={uploadingId === recordRow._id}
              disabled={uploadingId === recordRow._id}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRecordRow(null)}
            disabled={uploadingId === recordRow?._id}
          >
            סגור
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingDrop)}
        onClose={() => {
          if (uploadingId === pendingDrop?.rowId) return;
          clearPendingDrop();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          אישור העלאה לקוד {pendingDropRow?.code ?? ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {pendingDropRow?.text && (
              <Typography variant="body2" color="text.secondary">
                {pendingDropRow.text}
              </Typography>
            )}
            <Typography variant="body2">
              קובץ: <strong>{pendingDrop?.file?.name}</strong>
              {pendingDrop?.file?.size != null && (
                <> ({formatFileSize(pendingDrop.file.size)})</>
              )}
            </Typography>
            {pendingDrop?.objectUrl && (
              <Box
                component="audio"
                controls
                src={pendingDrop.objectUrl}
                sx={{ width: '100%', mt: 0.5 }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={clearPendingDrop}
            disabled={uploadingId === pendingDrop?.rowId}
          >
            בטל
          </Button>
          <Button
            variant="contained"
            onClick={confirmDropUpload}
            disabled={uploadingId === pendingDrop?.rowId}
            startIcon={
              uploadingId === pendingDrop?.rowId
                ? <CircularProgress size={16} color="inherit" />
                : null
            }
          >
            {uploadingId === pendingDrop?.rowId ? 'מעלה...' : 'אשר העלאה'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NarrationCatalog;
